import { useMemo } from "react";
import {
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  DocumentType,
  FragmentType,
  getFragmentData,
  gql,
} from "__generated__/gql";
import { SkuComponentType } from "__generated__/gql/graphql";
import ChakraReactTable from "components/Shared/ChakraReactTable";
import { useChakraReactTableProps } from "components/Shared/ChakraReactTable/utils";
import { formatUpc } from "components/Shared/Forms/UpcInput";
import { formatWeight, sortSkuComponents } from "components/Products/utils";
import { formatInteger } from "utils/formatting";
import CopyableCode from "components/Shared/CopyableCode";
import EmptyPlaceholder, { NA } from "components/Shared/EmptyPlaceholder";
import useStickyHeader from "components/Shared/ChakraReactTable/useStickyHeader";
import useStaticColumnPinning from "components/Shared/ChakraReactTable/useStaticColumnPinning";
import ScrollContainer from "components/Shared/ChakraReactTable/ScrollContainer";
import { useGetTransparency } from "utils/colorHooks";
import { SKU_COMPONENT_TYPE_LABELS_SHORT } from "components/Products/SkuPanel/SkuComponents/utils";

const fragment = gql(/* GraphQL */ `
  fragment SkuComponentsTable on Sku {
    id
    productLine {
      id
      innerCaseQuantity
      wholesaleCaseQuantity
      wholesaleCaseQuantityUnits
    }
    skuComponentsList {
      id
      skuComponentType
      upc
      grossWeightLbs
      dimensions {
        id
        formatted
      }
    }
  }
`);

type SkuComponentDatum = NonNullable<
  DocumentType<typeof fragment>
>["skuComponentsList"][number] & {
  productLine:
    | NonNullable<DocumentType<typeof fragment>>["productLine"]
    | null
    | undefined;
};

const col = createColumnHelper<SkuComponentDatum>();

const allColumns = [
  col.accessor("skuComponentType", {
    header: "",
    cell: ({ getValue }) => SKU_COMPONENT_TYPE_LABELS_SHORT[getValue()],
    size: 130,
    meta: {
      pinned: "left",
      props: { textAlign: "left", fontWeight: 600 },
    },
  }),
  col.accessor(
    (row) => {
      const componentType = row.skuComponentType;

      if (componentType === SkuComponentType.UNIT) {
        return 1;
      }

      if (componentType === SkuComponentType.INNER) {
        return row.productLine?.innerCaseQuantity;
      }

      if (componentType === SkuComponentType.CASE) {
        return row.productLine?.wholesaleCaseQuantity;
      }

      return null;
    },
    {
      id: "quantity",
      header: "Quantity",
      cell: ({ getValue, row }) => {
        const value = getValue();
        const componentType = row.original.skuComponentType;

        if (componentType === SkuComponentType.UNIT) {
          return <EmptyPlaceholder>1</EmptyPlaceholder>;
        }

        return formatInteger(value) ?? <NA />;
      },
      size: 80,
    },
  ),
  col.accessor(
    (row) => {
      const componentType = row.skuComponentType;

      if (componentType === SkuComponentType.UNIT) {
        return 1;
      }

      if (componentType === SkuComponentType.INNER) {
        return row.productLine?.innerCaseQuantity;
      }

      if (componentType === SkuComponentType.CASE) {
        return row.productLine?.wholesaleCaseQuantityUnits;
      }

      return null;
    },
    {
      id: "units",
      header: "Units",
      cell: ({ getValue, row }) => {
        const value = getValue();
        const componentType = row.original.skuComponentType;

        if (componentType === SkuComponentType.UNIT) {
          return <EmptyPlaceholder>1</EmptyPlaceholder>;
        }

        return formatInteger(value) ?? <NA />;
      },
      size: 80,
    },
  ),
  col.accessor("upc", {
    header: "UPC",
    cell: ({ getValue }) => {
      const upc = getValue();
      if (!upc) return <NA />;
      return <CopyableCode code={formatUpc(upc)} />;
    },
    size: 150,
  }),
  col.accessor("grossWeightLbs", {
    header: "Gross Weight",
    cell: ({ getValue }) => formatWeight(getValue()) ?? <NA />,
    size: 120,
    meta: { cellProps: { whiteSpace: "nowrap" } },
  }),
  col.display({
    id: "dimensions",
    header: "Dimensions",
    cell: ({ row }) => row.original.dimensions?.formatted ?? <NA />,
    size: 120,
    meta: { cellProps: { whiteSpace: "nowrap" } },
  }),
];

type Props = {
  sku: FragmentType<typeof fragment> | null | undefined;
  isLoading?: boolean;
};

export default function SkuComponentsTable({
  sku: skuFragment,
  isLoading,
}: Props) {
  const sku = getFragmentData(fragment, skuFragment);

  const hasInnerCaseQuantity = !!sku?.productLine?.innerCaseQuantity;

  const data: SkuComponentDatum[] = useMemo(
    () =>
      sortSkuComponents(
        sku?.skuComponentsList?.filter(
          (sc) =>
            sc.skuComponentType !== SkuComponentType.INNER ||
            hasInnerCaseQuantity,
        ) || [],
      ).map((component) => ({
        ...component,
        productLine: sku?.productLine,
      })),
    [sku?.skuComponentsList, sku?.productLine, hasInnerCaseQuantity],
  );

  const columns = useMemo(
    () =>
      allColumns.filter(
        (column) => column.id !== "units" || hasInnerCaseQuantity,
      ),
    [hasInnerCaseQuantity],
  );

  const columnPinning = useStaticColumnPinning(columns);

  const tableInstance = useReactTable<SkuComponentDatum>({
    columns,
    data,
    getCoreRowModel: getCoreRowModel(),
    state: { columnPinning },
  });

  const getTransparency = useGetTransparency();

  const tableProps = useChakraReactTableProps<SkuComponentDatum>([
    useStickyHeader(),
    {
      getColumnProps: () => ({ textAlign: "center" }),
      tableProps: {
        variant: "labeledValue",
        mt: 2,
        mb: 3,
        mx: 6,
        sx: {
          th: { textAlign: "center", verticalAlign: "bottom" },
          'th[data-pinning="left"][data-pinning-index-inside="0"], td[data-pinning="left"][data-pinning-index-inside="0"]':
            {
              backgroundColor: getTransparency("gray.200", 0.8),
              boxShadow: "none",
            },
        },
      },
      tableHeadProps: { whiteSpace: "nowrap" },
      tableBodyProps: {
        fontSize: ".9em",
        sx: { tr: { borderColor: "gray.300" } },
      },
    },
  ]);

  return (
    <ScrollContainer
      py={3}
      my={4}
      mx={-6}
      isNoBackground
      backgroundColor={getTransparency("gray.200", 0.9)}
      borderColor="gray.300"
      borderTopWidth={1}
      borderBottomWidth={1}
    >
      <ChakraReactTable
        tableInstance={tableInstance}
        {...tableProps}
        isLoading={isLoading}
      />
    </ScrollContainer>
  );
}
